import { createAsyncStoragePersister } from '@tanstack/query-async-storage-persister'
import { defaultShouldDehydrateQuery } from '@tanstack/react-query'
import { type PersistQueryClientOptions } from '@tanstack/react-query-persist-client'
import { MILLIS } from '@repo/constants'
import { del, get, set } from 'idb-keyval'

/** The single IndexedDB key the whole dehydrated cache is written under. */
const CACHE_KEY = 'fuze-query-cache'

/**
 * The CRT welcome page's query-key roots that are safe to persist across a reload: import-driven summary
 * data that is static within a session, plus reference data (`materialGroups`). The `varianceAnalysis*`
 * family (the stakeholder cards) is matched by prefix. Deliberately EXCLUDES the shared `pnl`
 * (actuals/forecasts) key — the P&L table reuses it and its Refresh-prices flow rematerialises under the
 * same key, so a persisted copy could show pre-refresh prices after a reload.
 */
const PERSISTED_QUERY_ROOTS = new Set<string>([
  'crtVersions',
  'pnlByCategory',
  'assumptionsWalk',
  'lmcTargets',
  'perChannelOverview',
  'perChannelBreakdown',
  'multiSopOverview',
  'multiSopBreakdown',
  'materialGroups',
])

/**
 * IndexedDB-backed persister (via `idb-keyval`) rather than localStorage: the P&L payloads are
 * whole KPI trees that would blow past localStorage's ~5 MB budget.
 */
const persister = createAsyncStoragePersister({
  storage: {
    getItem: (key) => get(key),
    setItem: (key, value) => set(key, value),
    removeItem: (key) => del(key),
  },
  key: CACHE_KEY,
})

/**
 * Persist options for `PersistQueryClientProvider`. `buster` is the build's git SHA so a deploy discards a
 * stale cache shape; `maxAge` caps how long a restored cache is trusted before it is dropped.
 */
export const persistOptions: Omit<PersistQueryClientOptions, 'queryClient'> = {
  persister,
  maxAge: MILLIS.DAY,
  buster: import.meta.env.VITE_SENTRY_RELEASE ?? 'dev',
  dehydrateOptions: {
    // A successful query is persisted only when its root is an allow-listed CRT welcome key. The `query`
    // type is inferred from `dehydrateOptions` so it always matches `defaultShouldDehydrateQuery`.
    shouldDehydrateQuery: (query) => {
      if (!defaultShouldDehydrateQuery(query)) return false
      const root = query.queryKey[0]
      if (typeof root !== 'string') return false
      return PERSISTED_QUERY_ROOTS.has(root) || root.startsWith('varianceAnalysis')
    },
  },
}

/**
 * Wipes the persisted cache from IndexedDB. Called on logout and again on sign-in (in `process-login`) so a
 * shared machine starts each user cold — the sign-in clear also covers a prior session that expired without
 * an explicit logout.
 */
export async function clearPersistedCache(): Promise<void> {
  await del(CACHE_KEY)
}
